<template>
  <app-popup :visible="props.visible" @cancel="closePopup">
    <div class="tw-bg-white tw-rounded-lg tw-shadow-lg tw-w-full tw-max-w-2xl tw-mx-auto">
      <!-- Header -->
      <div class="tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
        <div class="tw-flex tw-items-center tw-justify-between">
          <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-flex tw-items-center">
            <icon icon="solar:danger-triangle-bold" class="tw-text-amber-500 ltr:tw-mr-2 rtl:tw-ml-2 tw-text-xl" />
            {{ t('common.notification_types.losing_products.title') }}
          </h3>
          <button @click="closePopup" class="tw-text-gray-400 hover:tw-text-gray-500 tw-p-1">
            <icon icon="solar:close-circle-bold" class="tw-text-lg" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="tw-px-6 tw-py-4">
        <div class="ltr:tw-border-l-4 rtl:tw-border-r-4 tw-border-amber-400 tw-bg-amber-50 tw-p-4 tw-mb-4">
          <div class="tw-flex">
            <div class="tw-flex-shrink-0">
              <icon icon="solar:warning-bold" class="tw-text-amber-500 tw-h-5 tw-w-5" />
            </div>
            <div class="ltr:tw-ml-3 rtl:tw-mr-3">
              <p class="tw-text-sm tw-text-amber-700">
                {{ t('common.notification_types.losing_products.description') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Products Table -->
        <div class="tw-overflow-x-auto tw-shadow tw-ring-1 tw-ring-black tw-ring-opacity-5 tw-rounded-lg">
          <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
            <thead class="tw-bg-gray-50">
              <tr>
                <th scope="col" class="tw-px-6 tw-py-3 ltr:tw-text-left rtl:tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                  {{ t('common.notification_types.losing_products.product_name') }}
                </th>
                <th scope="col" class="tw-px-6 tw-py-3 ltr:tw-text-left rtl:tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                  {{ t('common.notification_types.losing_products.total_profit') }}
                </th>
                <th scope="col" class="tw-px-6 tw-py-3 ltr:tw-text-left rtl:tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">
                  {{ t('common.notification_types.losing_products.actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
              <tr v-for="product in props.products" :key="product.id" class="hover:tw-bg-gray-50">
                <td class="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-900">
                  {{ product.name }}
                </td>
                <td class="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-red-600 tw-font-medium">
                  {{ formatCurrency(product.total_profit) }}
                </td>
                <td class="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                  <button 
                    @click="navigateToProduct(product.id)" 
                    class="tw-text-blue-600 hover:tw-text-blue-800 tw-font-medium tw-text-sm"
                  >
                    {{ t('common.notification_types.losing_products.view_product') }}
                  </button>
                </td>
              </tr>
              <tr v-if="props.products.length === 0">
                <td colspan="3" class="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-gray-500 tw-text-center">
                  {{ t('common.notification_types.losing_products.no_products') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Footer -->
      <div class="tw-px-6 tw-py-4 tw-bg-gray-50 ltr:tw-text-right rtl:tw-text-left tw-rounded-b-lg">
        <button 
          @click="closePopup" 
          class="tw-inline-flex tw-items-center tw-justify-center tw-py-2 tw-px-4 tw-border tw-border-gray-300 tw-shadow-sm tw-text-sm tw-font-medium tw-rounded-md tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-blue-500"
        >
          {{ t('common.notification_types.losing_products.close') }}
        </button>
      </div>
    </div>
  </app-popup>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AppPopup from '@/components/AppPopup.vue';

const { t } = useI18n();
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  products: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close']);
const router = useRouter();

// Close the popup
const closePopup = () => {
  emit('close');
};

// Navigate to product details page
const navigateToProduct = (productId) => {
  router.push({ name: 'pages.admin.products.show', params: { id: productId } });
  closePopup();
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};
</script>
